import React, { useState, useEffect, useContext } from "react";
import { useNavigate, useParams } from "react-router-dom";
import InnerLayout from "../../../components/layouts/inner-layout";
import { DataContext } from "../../../components/layouts/main-layout";
import { useHotkeys } from "../../../components/helper-components/use-hotkeys";

const ViewRegionalRestriction = () => {
    const { id } = useParams();
    const { permissionFn } = useContext(DataContext);
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [restrictionData, setRestrictionData] = useState(null);

    useEffect(() => {
        // Simulate API call to fetch restriction data
        setTimeout(() => {
            setRestrictionData({
                id: id,
                store_code: "S0001",
                store_name: "Mega Mart Central",
                city: "New York",
                region: "Northeast",
                country: "USA",
                restricted_items: [
                    { code: "I101", name: "Milk 1L", category: "Dairy" },
                    { code: "I102", name: "Bread Wholewheat", category: "Bakery" },
                ],
                restricted_transactions: ["Sales Invoice", "Sales Return"],
                status: "Active",
                effective_from: "2024-01-01",
                effective_to: "2024-12-31",
                description: "Regional restrictions for dairy products in the Northeast region. These restrictions apply to specific dairy and bakery items to comply with local regulations and market conditions.",
                created_by: "admin@zota.com",
                created_date: "2024-01-15",
                last_modified_by: "admin@zota.com",
                last_modified_date: "2024-01-20",
            });
            setLoading(false);
        }, 1000);
    }, [id]);

    const handleEdit = () => {
        navigate(`/edit-regional-restriction/${id}`);
    };

    const handleBack = () => {
        navigate("/regional-restrictions");
    };

    const handleDelete = () => {
        if (window.confirm("Are you sure you want to delete this restriction?")) {
            // Handle delete logic
            navigate("/regional-restrictions");
        }
    };

    // Hotkeys
    useHotkeys("e", () => handleEdit(), { enableOnFormTags: true });
    useHotkeys("escape", () => handleBack());

    if (loading) {
        return (
            <InnerLayout onclkFn={"/inventory"} title="View Regional Restriction">
                <div className="p-5">
                    <div className="bg-[var(--tertiary-color)] border border-[var(--layout-border-color)] rounded-lg p-6">
                        <div className="animate-pulse">
                            <div className="h-4 bg-[var(--header-button-bg-color)] rounded w-1/4 mb-4"></div>
                            <div className="space-y-3">
                                <div className="h-4 bg-[var(--header-button-bg-color)] rounded"></div>
                                <div className="h-4 bg-[var(--header-button-bg-color)] rounded w-5/6"></div>
                                <div className="h-4 bg-[var(--header-button-bg-color)] rounded w-4/6"></div>
                            </div>
                        </div>
                    </div>
                </div>
            </InnerLayout>
        );
    }

    if (!restrictionData) {
        return (
            <InnerLayout onclkFn={"/inventory"} title="View Regional Restriction">
                <div className="p-5">
                    <div className="bg-[var(--tertiary-color)] border border-[var(--layout-border-color)] rounded-lg p-6">
                        <p className="text-[var(--description-color)]">Restriction not found</p>
                    </div>
                </div>
            </InnerLayout>
        );
    }

    return (
        <InnerLayout onclkFn={"/inventory"} title="View Regional Restriction">
            <div className="p-5">
                <div className="bg-[var(--tertiary-color)] border border-[var(--layout-border-color)] rounded-lg overflow-hidden">
                    {/* Header */}
                    <div className="bg-[var(--header-button-bg-color)] px-6 py-4 border-b border-[var(--layout-border-color)]">
                        <div className="flex justify-between items-center">
                            <h2 className="text-[length:var(--top-nav-heading-size)] font-[var(--top-nav-heading-weight)] text-[var(--top-nav-heading-color)]">
                                Regional Restriction Details
                            </h2>
                            <div className="flex space-x-3">
                                <button
                                    onClick={handleEdit}
                                    className="px-4 py-2 text-sm font-medium text-[var(--action-button-edit-color)] bg-[var(--action-button-edit-bg)] border border-[var(--action-button-edit-border)] rounded-md hover:bg-[var(--action-button-edit-bg-hover)] focus:outline-none focus:ring-2 focus:ring-[var(--action-button-edit-focus)] focus:border-[var(--action-button-edit-focus)]"
                                >
                                    Edit (E)
                                </button>
                                <button
                                    onClick={handleDelete}
                                    className="px-4 py-2 text-sm font-medium text-[var(--action-button-delete-color)] bg-[var(--action-button-delete-bg)] border border-[var(--action-button-delete-border)] rounded-md hover:bg-[var(--action-button-delete-bg-hover)] focus:outline-none focus:ring-2 focus:ring-[var(--action-button-delete-focus)] focus:border-[var(--action-button-delete-focus)]"
                                >
                                    Delete
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Content */}
                    <div className="p-6 space-y-6">
                        {/* Store Information */}
                        <div>
                            <h3 className="text-[length:var(--input-label-font-size)] font-[var(--input-label-font-weight)] text-[var(--top-nav-heading-color)] mb-4">Store Information</h3>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                <div>
                                    <label className="block text-[length:var(--input-label-font-size)] font-[var(--input-label-font-weight)] text-[var(--input-label-color)] mb-1">Store Code</label>
                                    <p className="text-[length:var(--input-font-size)] text-[var(--input-color)] font-medium">{restrictionData.store_code}</p>
                                </div>
                                <div>
                                    <label className="block text-[length:var(--input-label-font-size)] font-[var(--input-label-font-weight)] text-[var(--input-label-color)] mb-1">Store Name</label>
                                    <p className="text-[length:var(--input-font-size)] text-[var(--input-color)]">{restrictionData.store_name}</p>
                                </div>
                                <div>
                                    <label className="block text-[length:var(--input-label-font-size)] font-[var(--input-label-font-weight)] text-[var(--input-label-color)] mb-1">City</label>
                                    <p className="text-[length:var(--input-font-size)] text-[var(--input-color)]">{restrictionData.city}</p>
                                </div>
                            </div>
                        </div>

                        {/* Region Information */}
                        <div>
                            <h3 className="text-[length:var(--input-label-font-size)] font-[var(--input-label-font-weight)] text-[var(--top-nav-heading-color)] mb-4">Region Information</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-[length:var(--input-label-font-size)] font-[var(--input-label-font-weight)] text-[var(--input-label-color)] mb-1">Region</label>
                                    <p className="text-[length:var(--input-font-size)] text-[var(--input-color)]">{restrictionData.region}</p>
                                </div>
                                <div>
                                    <label className="block text-[length:var(--input-label-font-size)] font-[var(--input-label-font-weight)] text-[var(--input-label-color)] mb-1">Country</label>
                                    <p className="text-[length:var(--input-font-size)] text-[var(--input-color)]">{restrictionData.country}</p>
                                </div>
                            </div>
                        </div>

                        {/* Date Range */}
                        <div>
                            <h3 className="text-[length:var(--input-label-font-size)] font-[var(--input-label-font-weight)] text-[var(--top-nav-heading-color)] mb-4">Effective Period</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-[length:var(--input-label-font-size)] font-[var(--input-label-font-weight)] text-[var(--input-label-color)] mb-1">Effective From</label>
                                    <p className="text-[length:var(--input-font-size)] text-[var(--input-color)]">{restrictionData.effective_from}</p>
                                </div>
                                <div>
                                    <label className="block text-[length:var(--input-label-font-size)] font-[var(--input-label-font-weight)] text-[var(--input-label-color)] mb-1">Effective To</label>
                                    <p className="text-[length:var(--input-font-size)] text-[var(--input-color)]">{restrictionData.effective_to || "No end date"}</p>
                                </div>
                            </div>
                        </div>

                        {/* Status */}
                        <div>
                            <h3 className="text-[length:var(--input-label-font-size)] font-[var(--input-label-font-weight)] text-[var(--top-nav-heading-color)] mb-4">Status</h3>
                            <span
                                className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${restrictionData.status === "Active"
                                    ? "bg-[var(--status-badge-green-bg)] text-[var(--status-badge-green-fg)]"
                                    : restrictionData.status === "Cancelled"
                                        ? "bg-[var(--status-badge-red-bg)] text-[var(--status-badge-red-fg)]"
                                        : "bg-[var(--status-badge-yellow-bg)] text-[var(--status-badge-yellow-fg)]"
                                    }`}
                            >
                                {restrictionData.status}
                            </span>
                        </div>

                        {/* Restricted Items */}
                        <div>
                            <h3 className="text-[length:var(--input-label-font-size)] font-[var(--input-label-font-weight)] text-[var(--top-nav-heading-color)] mb-4">Restricted Items</h3>
                            {restrictionData.restricted_items.length > 0 ? (
                                <div className="bg-[var(--header-button-bg-color)] rounded-lg p-4">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        {restrictionData.restricted_items.map((item, index) => (
                                            <div key={index} className="flex items-center space-x-3 p-3 bg-white rounded border">
                                                <div className="w-3 h-3 bg-red-400 rounded-full"></div>
                                                <div>
                                                    <p className="text-[length:var(--input-font-size)] font-medium text-[var(--input-color)]">{item.code} - {item.name}</p>
                                                    <p className="text-xs text-[var(--description-color)]">{item.category}</p>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ) : (
                                <p className="text-[length:var(--input-font-size)] text-[var(--description-color)]">No items restricted</p>
                            )}
                        </div>

                        {/* Restricted Transactions */}
                        <div>
                            <h3 className="text-[length:var(--input-label-font-size)] font-[var(--input-label-font-weight)] text-[var(--top-nav-heading-color)] mb-4">Restricted Transactions</h3>
                            {restrictionData.restricted_transactions.length > 0 ? (
                                <div className="bg-[var(--header-button-bg-color)] rounded-lg p-4">
                                    <div className="flex flex-wrap gap-2">
                                        {restrictionData.restricted_transactions.map((transaction, index) => (
                                            <span
                                                key={index}
                                                className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-[var(--status-badge-blue-bg)] text-[var(--status-badge-blue-fg)]"
                                            >
                                                {transaction}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            ) : (
                                <p className="text-[length:var(--input-font-size)] text-[var(--description-color)]">No transactions restricted</p>
                            )}
                        </div>

                        {/* Description */}
                        <div>
                            <h3 className="text-[length:var(--input-label-font-size)] font-[var(--input-label-font-weight)] text-[var(--top-nav-heading-color)] mb-4">Description</h3>
                            <div className="bg-[var(--header-button-bg-color)] rounded-lg p-4">
                                <p className="text-[length:var(--input-font-size)] text-[var(--input-color)]">{restrictionData.description}</p>
                            </div>
                        </div>

                        {/* Audit Information */}
                        <div>
                            <h3 className="text-[length:var(--input-label-font-size)] font-[var(--input-label-font-weight)] text-[var(--top-nav-heading-color)] mb-4">Audit Information</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-[length:var(--input-label-font-size)] font-[var(--input-label-font-weight)] text-[var(--input-label-color)] mb-1">Created By</label>
                                    <p className="text-[length:var(--input-font-size)] text-[var(--input-color)]">{restrictionData.created_by}</p>
                                </div>
                                <div>
                                    <label className="block text-[length:var(--input-label-font-size)] font-[var(--input-label-font-weight)] text-[var(--input-label-color)] mb-1">Created Date</label>
                                    <p className="text-[length:var(--input-font-size)] text-[var(--input-color)]">{restrictionData.created_date}</p>
                                </div>
                                <div>
                                    <label className="block text-[length:var(--input-label-font-size)] font-[var(--input-label-font-weight)] text-[var(--input-label-color)] mb-1">Last Modified By</label>
                                    <p className="text-[length:var(--input-font-size)] text-[var(--input-color)]">{restrictionData.last_modified_by}</p>
                                </div>
                                <div>
                                    <label className="block text-[length:var(--input-label-font-size)] font-[var(--input-label-font-weight)] text-[var(--input-label-color)] mb-1">Last Modified Date</label>
                                    <p className="text-[length:var(--input-font-size)] text-[var(--input-color)]">{restrictionData.last_modified_date}</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Footer */}
                    <div className="bg-[var(--header-button-bg-color)] px-6 py-4 border-t border-[var(--layout-border-color)]">
                        <div className="flex justify-end">
                            <button
                                onClick={handleBack}
                                className="px-4 py-2 text-sm font-medium text-[var(--header-button-fg-color)] bg-[var(--tertiary-color)] border border-[var(--input-border)] rounded-md hover:bg-[var(--header-button-hover-color)] focus:outline-none focus:ring-2 focus:ring-[var(--input-focus)] focus:border-[var(--input-focus)]"
                            >
                                Back to List
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </InnerLayout>
    );
};

export default ViewRegionalRestriction;
